import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import type { FeedEvent } from '@/lib/api/apiTypes';
import { getEventHref } from '@/lib/navigation/routes';
import { router } from 'expo-router';
import { MapPinIcon, NavigationIcon } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Pressable, View } from 'react-native';

export type WebFeedRailEvent = {
  event: FeedEvent;
  label: string;
  score: number;
};

export type WebFeedRailScene = {
  tag: string;
  label: string;
  count: number;
};

export function WebFeedRail({
  hotItems,
  sceneItems,
  venueCount,
}: {
  hotItems: WebFeedRailEvent[];
  sceneItems: WebFeedRailScene[];
  venueCount: number;
}) {
  return (
    <View className="w-[230px] shrink-0 gap-6 border-l border-border/70 pl-6">
      {hotItems.length > 0 ? (
        <RailSection label="Hot picks">
          <View className="gap-2">
            {hotItems.map((item) => (
              <Pressable
                key={item.event.id}
                className="gap-1.5 rounded-2xl bg-surface-raised/70 px-3 py-2.5 active:opacity-75"
                onPress={() =>
                  router.push(
                    getEventHref(item.event.id, item.event.title, { source: 'web_rail' }) as any
                  )
                }>
                <View className="flex-row items-center justify-between gap-2">
                  <Text className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary">
                    {item.label}
                  </Text>
                  <Text className="text-[10px] font-semibold text-muted-foreground">
                    {item.event.going_count} going
                  </Text>
                </View>
                <Text
                  className="text-[12px] font-semibold leading-4 text-foreground"
                  numberOfLines={2}>
                  {item.event.title}
                </Text>
                <Text className="text-[11px] text-muted-foreground" numberOfLines={1}>
                  {item.event.venue_name}
                </Text>
              </Pressable>
            ))}
          </View>
        </RailSection>
      ) : null}

      {sceneItems.length > 0 ? (
        <RailSection label="Popular scenes">
          <View className="flex-row flex-wrap gap-2">
            {sceneItems.map((item) => (
              <View key={item.tag} className="rounded-full bg-surface-raised px-2.5 py-1">
                <Text className="text-[11px] font-medium text-muted-foreground">{item.label}</Text>
              </View>
            ))}
          </View>
        </RailSection>
      ) : null}

      <Pressable
        className="flex-row items-center justify-between gap-3 border-t border-border/70 pt-5 active:opacity-75"
        onPress={() => router.navigate('/(tabs)/map' as never)}>
        <View className="flex-row items-center gap-2.5">
          <View className="size-7 items-center justify-center rounded-full bg-surface-raised">
            <Icon as={MapPinIcon} size={14} className="text-primary" />
          </View>
          <Text className="text-[12px] font-semibold text-muted-foreground">
            {venueCount} venue{venueCount === 1 ? '' : 's'} nearby
          </Text>
        </View>
        <Icon as={NavigationIcon} size={13} className="text-muted-foreground" />
      </Pressable>
    </View>
  );
}

function RailSection({ children, label }: { children: ReactNode; label: string }) {
  return (
    <View className="gap-3">
      <Text className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </Text>
      {children}
    </View>
  );
}
